import { useEffect, useRef, useState } from 'react';

//hooks
import { usePrefersReducedMotion } from '@/hooks/usePrefersReducedMotion';

//components
import {
  Kicker,
  KickerDot,
  SectionTitle,
  SectionLede,
} from '@/containers-ai/SectionHeading';

//styles
import {
  WorkflowSection,
  PodStage,
  PodHead,
  PodTitle,
  PodChip,
  GraphWrap,
  GraphSvg,
  NodeGroup,
  EdgePath,
  EdgeLabel,
  PodFoot,
  ReplayButton,
} from './AgenticWorkflow.styles';
import { Container } from '@/components/Grid';

const GRAD_ID = 'agenticWorkflowGrad';
const STEP_INTERVAL = 1050;

// Non-translatable node layout (position, size, color) — merged at render time
// with the translatable title/subtitle/caption text from `content.nodes`,
// keyed by the same node ids.
const NODE_LAYOUT = [
  {
    id: 'trigger',
    x: 30,
    y: 118,
    w: 160,
    h: 56,
    rx: 14,
    fill: '#0E2A31',
    stroke: '#2A5B60',
    title: { x: 110, y: 150, fontSize: 12, fontWeight: 700, fill: '#EAF4F1' },
    caption: { x: 110, y: 192 },
  },
  {
    id: 'agent',
    x: 250,
    y: 112,
    w: 210,
    h: 68,
    rx: 16,
    fill: '#0E2A31',
    agent: true,
    title: { x: 355, y: 141, fontSize: 13, fontWeight: 800, fill: '#EAF4F1' },
    subtitle: {
      x: 355,
      y: 160,
      fontSize: 10,
      fontWeight: 600,
      fill: '#84F11E',
    },
  },
  {
    id: 'branch',
    x: 540,
    y: 118,
    w: 120,
    h: 56,
    rx: 12,
    fill: '#0E2A31',
    stroke: '#2A5B60',
    title: { x: 600, y: 143, fontSize: 11, fontWeight: 700, fill: '#EAF4F1' },
    subtitle: {
      x: 600,
      y: 160,
      fontSize: 11,
      fontWeight: 700,
      fill: '#EAF4F1',
    },
  },
  {
    id: 'write',
    x: 770,
    y: 50,
    w: 210,
    h: 56,
    rx: 14,
    fill: '#0E2A31',
    stroke: '#2A5B60',
    title: { x: 875, y: 83, fontSize: 12, fontWeight: 700, fill: '#EAF4F1' },
    caption: { x: 875, y: 124 },
  },
  {
    id: 'expert',
    x: 770,
    y: 214,
    w: 210,
    h: 56,
    rx: 14,
    fill: '#0E2A31',
    stroke: '#2A5B60',
    title: { x: 875, y: 247, fontSize: 12, fontWeight: 700, fill: '#EAF4F1' },
    caption: { x: 875, y: 288 },
  },
  {
    id: 'model',
    x: 60,
    y: 352,
    w: 120,
    h: 50,
    rx: 12,
    fill: '#0A222A',
    stroke: '#2A5B60',
    title: { x: 120, y: 381, fontSize: 11, fontWeight: 600, fill: '#EAF4F1' },
    caption: { x: 120, y: 420 },
  },
  {
    id: 'memory',
    x: 230,
    y: 352,
    w: 120,
    h: 50,
    rx: 12,
    fill: '#0A222A',
    stroke: '#2A5B60',
    title: { x: 290, y: 381, fontSize: 11, fontWeight: 600, fill: '#EAF4F1' },
    caption: { x: 290, y: 420 },
  },
  {
    id: 'voice',
    x: 400,
    y: 352,
    w: 120,
    h: 50,
    rx: 12,
    fill: '#0A222A',
    stroke: '#2A5B60',
    title: { x: 460, y: 381, fontSize: 11, fontWeight: 600, fill: '#EAF4F1' },
    caption: { x: 460, y: 420 },
  },
  {
    id: 'email',
    x: 570,
    y: 352,
    w: 120,
    h: 50,
    rx: 12,
    fill: '#0A222A',
    stroke: '#2A5B60',
    title: { x: 630, y: 381, fontSize: 11, fontWeight: 600, fill: '#EAF4F1' },
    caption: { x: 630, y: 420 },
  },
  {
    id: 'ocr',
    x: 740,
    y: 352,
    w: 120,
    h: 50,
    rx: 12,
    fill: '#0A222A',
    stroke: '#2A5B60',
    title: { x: 800, y: 381, fontSize: 11, fontWeight: 600, fill: '#EAF4F1' },
    caption: { x: 800, y: 420 },
  },
];

const EDGES = [
  { id: 'agent', d: 'M190 146 C 218 146 224 146 250 146' },
  { id: 'branch', d: 'M460 146 C 498 146 505 146 540 146' },
  { id: 'write', d: 'M660 134 C 706 128 728 78 770 78' },
  { id: 'expert', d: 'M660 158 C 706 168 728 242 770 242', dim: true },
  { id: 'e_model', d: 'M345 180 C 300 250 160 300 120 350', sub: true },
  { id: 'e_memory', d: 'M350 180 C 335 250 300 300 290 350', sub: true },
  { id: 'e_voice', d: 'M362 180 C 380 250 440 300 460 350', sub: true },
  { id: 'e_email', d: 'M368 180 C 430 250 580 300 630 350', sub: true },
  { id: 'e_ocr', d: 'M374 180 C 470 250 720 300 800 350', sub: true },
];

const STEPS = [
  { nodes: ['trigger'], edges: [] },
  {
    nodes: ['agent', 'model', 'memory'],
    edges: ['agent', 'e_model', 'e_memory'],
  },
  { nodes: ['voice'], edges: ['e_voice'] },
  { nodes: ['email'], edges: ['e_email'] },
  { nodes: ['ocr'], edges: ['e_ocr'] },
  { nodes: ['branch'], edges: ['branch'] },
  { nodes: ['write'], edges: ['write'] },
];

export function AgenticWorkflow({ content }) {
  const NODES = NODE_LAYOUT.map((node) => {
    const nodeContent = content.nodes[node.id];
    return {
      ...node,
      title: { ...node.title, text: nodeContent.title },
      subtitle:
        node.subtitle && nodeContent.subtitle
          ? { ...node.subtitle, text: nodeContent.subtitle }
          : undefined,
      caption:
        node.caption && nodeContent.caption
          ? { ...node.caption, text: nodeContent.caption }
          : undefined,
    };
  });

  const prefersReducedMotion = usePrefersReducedMotion();
  const stageRef = useRef(null);
  const startedRef = useRef(false);
  const timerRef = useRef(null);
  const [activeNodes, setActiveNodes] = useState(new Set());
  const [activeEdges, setActiveEdges] = useState(new Set());
  const [done, setDone] = useState(false);

  function play() {
    if (timerRef.current) clearInterval(timerRef.current);
    setActiveNodes(new Set());
    setActiveEdges(new Set());
    setDone(false);

    if (prefersReducedMotion) {
      setActiveNodes(new Set(STEPS.flatMap((step) => step.nodes)));
      setActiveEdges(new Set(STEPS.flatMap((step) => step.edges)));
      setDone(true);
      return;
    }

    let i = 0;
    timerRef.current = setInterval(() => {
      if (i < STEPS.length) {
        const step = STEPS[i];
        setActiveNodes((prev) => new Set([...prev, ...step.nodes]));
        setActiveEdges((prev) => new Set([...prev, ...step.edges]));
        i += 1;
      } else {
        clearInterval(timerRef.current);
        timerRef.current = null;
        setDone(true);
      }
    }, STEP_INTERVAL);
  }

  useEffect(() => {
    if (!('IntersectionObserver' in window)) {
      play();
      return undefined;
    }
    const el = stageRef.current;
    if (!el) return undefined;

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !startedRef.current) {
          startedRef.current = true;
          play();
        }
      },
      { threshold: 0.3 }
    );
    io.observe(el);

    return () => {
      io.disconnect();
      if (timerRef.current) clearInterval(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefersReducedMotion]);

  return (
    <WorkflowSection id="agentic-workflow">
      <Container>
        <Kicker>
          <KickerDot />
          {content.kicker}
        </Kicker>
        <SectionTitle>{content.title}</SectionTitle>
        <SectionLede style={{ maxWidth: '565px' }}>{content.lede}</SectionLede>

        <PodStage ref={stageRef}>
          <PodHead>
            <PodTitle>{content.podTitle}</PodTitle>
            <PodChip $done={done}>
              {done ? content.statusResolved : content.statusWorking}
            </PodChip>
          </PodHead>

          <GraphWrap>
            <GraphSvg
              viewBox="0 0 1040 445"
              role="img"
              aria-label="The POD collection workflow as a node graph: a trigger fires when a POD is missing; the Loadsmart AI agent (with its chat model, memory, and voice, email and document-reading tools hanging below it) works the shipment; a decision node checks whether the POD was collected, then either writes it back to your system of record and closes the shipment, or routes to a Freight Expert."
            >
              <defs>
                <linearGradient id={GRAD_ID} x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0" stopColor="#00D7D7" />
                  <stop offset="1" stopColor="#84F11E" />
                </linearGradient>
              </defs>

              {EDGES.map((edge) => (
                <EdgePath
                  key={edge.id}
                  d={edge.d}
                  $sub={edge.sub}
                  $dim={edge.dim}
                  $active={activeEdges.has(edge.id)}
                />
              ))}

              <EdgeLabel x={700} y={104} textAnchor="middle">
                {content.trueLabel}
              </EdgeLabel>
              <EdgeLabel x={700} y={214} textAnchor="middle">
                {content.falseLabel}
              </EdgeLabel>

              {!prefersReducedMotion && (
                <circle r="3.2" fill="#00D7D7">
                  <animateMotion
                    dur="2s"
                    repeatCount="indefinite"
                    path="M190 146 C 218 146 224 146 250 146"
                  />
                </circle>
              )}

              {NODES.map((node) => (
                <NodeGroup
                  key={node.id}
                  fontFamily="Manrope,system-ui,sans-serif"
                  $agent={node.agent}
                  $gradId={GRAD_ID}
                  $active={activeNodes.has(node.id)}
                >
                  <rect
                    x={node.x}
                    y={node.y}
                    width={node.w}
                    height={node.h}
                    rx={node.rx}
                    fill={node.fill}
                    stroke={node.stroke}
                  />
                  <text
                    x={node.title.x}
                    y={node.title.y}
                    textAnchor="middle"
                    fontSize={node.title.fontSize}
                    fontWeight={node.title.fontWeight}
                    fill={node.title.fill}
                  >
                    {node.title.text}
                  </text>
                  {node.subtitle && (
                    <text
                      x={node.subtitle.x}
                      y={node.subtitle.y}
                      textAnchor="middle"
                      fontSize={node.subtitle.fontSize}
                      fontWeight={node.subtitle.fontWeight}
                      fill={node.subtitle.fill}
                    >
                      {node.subtitle.text}
                    </text>
                  )}
                  {node.caption && (
                    <EdgeLabel
                      x={node.caption.x}
                      y={node.caption.y}
                      textAnchor="middle"
                    >
                      {node.caption.text}
                    </EdgeLabel>
                  )}
                </NodeGroup>
              ))}
            </GraphSvg>
          </GraphWrap>

          <PodFoot>
            <span>{content.sessionLabel}</span>
            <ReplayButton type="button" onClick={play}>
              {content.replayLabel}
            </ReplayButton>
          </PodFoot>
        </PodStage>
      </Container>
    </WorkflowSection>
  );
}
